import { axiosGet, axiosPost, axiosPut } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery } from '@/hooks';
import type { z } from 'zod';
import { queryKeyMap } from '../../../../_utils/query-keys';
import {
  type RelatedMaterialFormSchema,
  RelatedMaterialModel,
  RelatedMaterialMutationParamsSchema,
  RelatedMaterialsModel,
} from '../../../_models/related-material';

export const useRelatedMaterials = (projectId: string) => {
  return useQuery({
    queryKey: queryKeyMap.relatedMaterials(projectId),
    queryFn: async () => {
      const res = await axiosGet(
        `/api/projects/${projectId}/related-materials/`,
      );

      return RelatedMaterialsModel.parse(res);
    },
    enabled: !!projectId,
  });
};

export const useCreateRelatedMaterial = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof RelatedMaterialFormSchema> & {
        attachmentIds: z.infer<
          typeof RelatedMaterialMutationParamsSchema
        >['attachmentIds'];
      },
    ) => {
      const { projectId, ...restValues } = values;
      const params = RelatedMaterialMutationParamsSchema.parse({
        ...restValues,
      });

      const res = await axiosPost(
        `/api/projects/${projectId}/related-materials/`,
        params,
      );

      await invalidate(queryKeyMap.relatedMaterials(projectId));

      return RelatedMaterialModel.parse(res);
    },
  });
};

export const useUpdateRelatedMaterial = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof RelatedMaterialFormSchema> & {
        id: z.infer<typeof RelatedMaterialModel.shape.id>;
        attachmentIds: z.infer<
          typeof RelatedMaterialMutationParamsSchema
        >['attachmentIds'];
      },
    ) => {
      const { projectId, id, ...restValues } = values;
      const params = RelatedMaterialMutationParamsSchema.parse({
        ...restValues,
      });
      const res = await axiosPut(
        `/api/projects/${projectId}/related-materials/${id}/`,
        params,
      );

      await invalidate(queryKeyMap.relatedMaterials(projectId));

      return RelatedMaterialModel.parse(res);
    },
  });
};
